import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { configAuth } from 'shared/app-config'
import type { CopyKey } from 'locales/copy-keys.gen'
import { ProjectTypes } from 'constants/enums'
import type { Project, ProjectSale } from 'models/project'
import { StoContentTypes, type ProjectContentItem } from 'models/projectContent'
import type { FastFact, TokenBalance } from 'models/investment'
import { useAuth } from 'shared/context/AuthContext'
import { getAuthState } from 'shared/utils/authState'
import { formatUtcDate } from 'utils/formatUtcDate'
import { useStoProject } from './useStoProject'
import { stoQueries } from './queries/stoQueries'

const isSet = (flag: boolean | string | null | undefined) => flag === true || flag === 'true'

/**
 * The open redemption sale, as investor-dashboard's Distributions tab decides it: the product
 * is redeemable, has a redemption sale, and today is inside that sale's window.
 */
export const openRedemptionSale = (
  project: Pick<Project, 'isRedeemable' | 'sales'> | undefined,
  now: number,
): ProjectSale | undefined => {
  if (!isSet(project?.isRedeemable)) return undefined
  const sale = project?.sales?.find((item) => isSet(item.isRedemption))
  if (!sale?.startDate || !sale.endDate) return undefined
  const opensAt = new Date(sale.startDate).getTime()
  const closesAt = new Date(sale.endDate).getTime()
  return opensAt < now && now < closesAt ? sale : undefined
}

/**
 * Notes the visitor can redeem: every wallet's holding of the token, collateral excluded,
 * rounded to the token's decimals so a sum of wallets never shows float noise (0.30000000000000004).
 */
export const heldNotes = (
  balances: TokenBalance[],
  project: Pick<Project, 'secondaryAssetId' | 'tokenSymbol' | 'tokenDecimals'>,
): number => {
  const total = balances
    .filter((item) => !item.collateralAmount)
    .filter(
      (item) =>
        (Boolean(project.secondaryAssetId) && item.asset === project.secondaryAssetId) ||
        (Boolean(project.tokenSymbol) && item.tokenSymbol === project.tokenSymbol),
    )
    .reduce((sum, item) => sum + (Number(item.balance) || 0), 0)
  return Number(total.toFixed(project.tokenDecimals ?? 8))
}

/** Fast facts in either back-office shape: one record holding a list, or a record per fact. */
export const collectFastFacts = (list: ProjectContentItem[] | undefined): FastFact[] =>
  (list ?? [])
    .filter((item) => item.type === StoContentTypes.FASTFACTS)
    .flatMap((item) => (Array.isArray(item.content) ? item.content : [item.content]))
    .filter((fact): fact is FastFact => Boolean(fact?.title && fact?.value))

/** "Redemption" or "Redemption period" — not "Redemption fee" or "Minimum redemption". */
const REDEMPTION_FACT = /^redemption( period)?$/i

export interface RedemptionTerm {
  labelKey: CopyKey
  titleKey: CopyKey
  value: string
  textKey: CopyKey
}

/**
 * What the card under the Redeem tab says. Funds redeem on a schedule, which back-office only
 * keeps as a fast fact ("Redemption: Quarterly"). Notes and the rest show the sale's maturity
 * date, else the day the redemption window closes.
 */
export const redemptionTerm = (
  project: Pick<Project, 'type'>,
  sale: ProjectSale,
  facts: FastFact[],
): RedemptionTerm | undefined => {
  if (project.type === ProjectTypes.FUND) {
    const frequency = facts.find((fact) => REDEMPTION_FACT.test(fact.title.trim()))?.value.trim()
    return frequency
      ? {
          labelKey: 'stoPage.keyActions.redeem.frequencyLabel',
          titleKey: 'stoPage.keyActions.redeem.valueTitle',
          value: frequency,
          textKey: 'stoPage.keyActions.redeem.frequencyNote',
        }
      : undefined
  }
  const maturity = formatUtcDate(sale.maturityDate)
  if (maturity) {
    return {
      labelKey: 'stoPage.keyActions.redeem.maturityDate',
      titleKey: 'stoPage.keyActions.redeem.valueTitle',
      value: maturity,
      textKey: 'stoPage.keyActions.redeem.maturityNote',
    }
  }
  const closes = formatUtcDate(sale.endDate)
  return closes
    ? {
        labelKey: 'stoPage.keyActions.redeem.windowLabel',
        titleKey: 'stoPage.keyActions.redeem.windowTitle',
        value: closes,
        textKey: 'stoPage.keyActions.redeem.windowNote',
      }
    : undefined
}

export interface RedeemHoldings {
  /** Token symbol, for the "CMSTR notes" label. */
  symbol: string
  notes: number
  /** Notes × the sale's redemption price; undefined when the sale has no price (funds today). */
  amount?: number
  /** The sale's payout currency, as the library currency config names it (`usdc-fb`, `lbtc`). */
  currency: string
}

export type RedeemPanelView =
  | { status: 'loading' }
  | { status: 'closed' }
  | {
      status: 'open'
      /** Undefined hides the card. */
      term?: RedemptionTerm
      /** Undefined when signed out, or when the balances could not be loaded. */
      holdings?: RedeemHoldings
      /** Signed in and holding notes; also when the balances failed, so the dashboard decides. */
      canRedeem: boolean
      /** Still waiting for the session or the balances. */
      isChecking: boolean
      /** Opens the dashboard's Distributions tab, where the redemption request is made. */
      onRedeem: () => void
    }

/** The dashboard is an in-app route (module federation), so it is navigated to, never linked absolutely. */
const DISTRIBUTIONS_PATH = `${configAuth.dashboardUrl ?? '/dashboard'}/distributions`

export function useRedeemPanel(): RedeemPanelView {
  const navigate = useNavigate()
  const { project, projectId, isPending } = useStoProject()
  const contentQuery = useQuery(stoQueries.content(projectId))
  const { user } = useAuth()
  const authState = getAuthState(user)
  const balancesQuery = useQuery(stoQueries.tokenBalances(authState === 'authenticated' ? user?._id : undefined))

  // Read once when the tab opens. It only renders in the browser (Invest is the server's default tab).
  const [now] = useState(Date.now)

  if (isPending || !project) return { status: 'loading' }
  const sale = openRedemptionSale(project, now)
  if (!sale) return { status: 'closed' }

  const isChecking = authState === 'resolving' || balancesQuery.isLoading
  const isSignedIn = authState === 'authenticated'
  const balances = balancesQuery.data
  const price = Number(sale.conversionPrice)
  const notes = balances ? heldNotes(balances, project) : 0
  return {
    status: 'open',
    term: redemptionTerm(project, sale, collectFastFacts(contentQuery.data)),
    isChecking,
    canRedeem: isSignedIn && !isChecking && (balancesQuery.isError || notes > 0),
    onRedeem: () => navigate(DISTRIBUTIONS_PATH),
    holdings:
      isSignedIn && !isChecking && balances
        ? {
            symbol: project.tokenSymbol ?? '',
            notes,
            amount: sale.conversionPrice && Number.isFinite(price) ? notes * price : undefined,
            currency: sale.currencies?.[0] ?? project.tokenCurrency ?? 'USD',
          }
        : undefined,
  }
}
